import { useCallback, useRef, useState, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { Lean } from "../ds/Beam"
import { Composer, type ComposerHandle } from "../landing/Composer"
import "../landing/landing.css"
import { track } from "@/lib/analytics"
import { APP_URL } from "./SiteLayout"

/**
 * The hero (handoff 5 §1): a question in on the left, the reading that comes back on the right.
 * A chip puts its sample into the composer and swaps the card, at once. The
 * composer stays live: typing replaces the sample and Ask runs it in the app.
 *
 * The cards are worked examples. The dates in the time-limit card are Nira's own (nira/law/time_limit:
 * FY 2019-20, a notice of 30 Nov 2024); the case names are made up, and the page says so.
 */

type Sample = "time" | "itc" | "notice" | "appeal"

const SAMPLES: Record<Sample, { chip: string; label: string; question: string; took: string; door?: "notice" | "appeal" }> = {
  time: {
    chip: "Time limit", label: "Is this s.74 notice time-barred?", took: "about 40 s",
    question: "An SCN under section 74 was issued on 30 Nov 2024 for FY 2019-20. Is it time-barred?",
  },
  itc: {
    chip: "ITC", label: "My supplier never filed. Do I lose the credit?", took: "about 50 s",
    question: "My supplier never filed GSTR-1. Can the officer reverse my ITC under section 16(2)(c)?",
  },
  notice: { chip: "A notice", label: "Drop a DRC-01", took: "about 3 min", question: "", door: "notice" },
  appeal: {
    chip: "An order", label: "Appeal, or pay?", took: "about 2 min", door: "appeal",
    question: "An order confirms ₹12,00,000 of tax under s.74. Should my client appeal, or pay?",
  },
}
const ORDER: Sample[] = ["time", "itc", "notice", "appeal"]
const SAMPLE_FILE = "DRC-01_Lakeview_Traders.pdf · 15 pages · attached"

/** The reading each sample gets back, as Nira shows it. */
function Card({ which }: { which: Sample }) {
  if (which === "time") return (
    <div className="ng-card" aria-label="A time-limit reading, as Nira shows it">
      <div className="ng-meta"><span className="nst-k">Research · time limit</span><span className="nst-mono">FY 2019-20 · notice 30 Nov 2024</span></div>
      <p className="ng-v">In time under s.74. Out of time the moment it falls to s.73.</p>
      <div className="ng-rows">
        <div className="ng-row"><span>s.74 · notice was due by <span className="nst-mono">30 Sep 2025</span></span><span className="ng-chip ok">in time</span></div>
        <div className="ng-row"><span>s.73 · order was due by <span className="nst-mono">31 Aug 2024</span></span><span className="ng-chip warn">out of time</span></div>
        <div className="ng-row"><span>Stands only if intent is made out · s.75(2)</span><span className="ng-chip">turns on intent</span></div>
      </div>
      <div className="ng-foot">
        <div><div className="ng-kk">Your first point</div><div className="ng-strong">Intent is the department's to prove</div></div>
        <div><div className="ng-kk">Computed from</div><div className="ng-plain">s.73(10), s.74(10), with the working</div></div>
      </div>
    </div>
  )
  if (which === "itc") return (
    <div className="ng-card" aria-label="A research answer, as Nira shows it">
      <div className="ng-meta"><span className="nst-k">Research</span><span className="nst-mono">Telangana · FY 2023-24 · s.74</span></div>
      <p className="ng-v">Your credit can be defended. Prove the goods reached you.</p>
      <div className="ng-lean"><Lean lean="helps" label={null} size="lg" title="Leans for you" className="nst-lean" /><span><b>Leans for you</b> · 2 help, 1 binds against</span></div>
      <div className="ng-rows">
        <div className="ng-row"><span><Lean lean="helps" label={null} title="Helps" className="nst-lean" /><span className="sr-only">Helps:</span><b>Sunvale Power</b> <em>· Calcutta HC · SLP dismissed</em></span><span className="ng-chip ok">persuasive here</span></div>
        <div className="ng-row"><span><Lean lean="helps" label={null} title="Helps" className="nst-lean" /><span className="sr-only">Helps:</span><b>Kaveri Bhavani Traders</b> <em>· Madras HC</em></span><span className="ng-chip ok">persuasive here</span></div>
        <div className="ng-row"><span><Lean lean="hurts" label={null} title="Hurts" className="nst-lean" /><span className="sr-only">Hurts:</span><b>Harborline Coffee</b> <em>· Supreme Court</em></span><span className="ng-chip warn">binds</span></div>
      </div>
      <div className="ng-foot">
        <div><div className="ng-kk">Reply by</div><div className="ng-vv">14 Nov 2026</div></div>
        <div><div className="ng-kk">At stake</div><div className="ng-vv">nil to ₹24.0L</div></div>
      </div>
    </div>
  )
  if (which === "notice") return (
    <div className="ng-card" aria-label="A notice analysis, as Nira shows it">
      <div className="ng-meta"><span className="nst-k">Notice analysis</span><span className="nst-mono">DRC-01 · s.74 · three years</span></div>
      <p className="ng-v">One year is out of time. The other two can be answered.</p>
      <div className="ng-rows">
        <div className="ng-row"><span className="nst-mono">FY 2019-20</span><span className="ng-chip warn">out of time</span></div>
        <div className="ng-row"><span className="nst-mono">FY 2022-23</span><span className="ng-chip">turns on intent</span></div>
        <div className="ng-row"><span className="nst-mono">FY 2023-24</span><span className="ng-chip ok">in time</span></div>
      </div>
      <div className="ng-foot">
        <div><div className="ng-kk">Worst case</div><div className="ng-vv">₹54.0L</div></div>
        <div><div className="ng-kk">Pay now to settle</div><div className="ng-vv">₹21.3L</div></div>
      </div>
      <p className="ng-aside">The officer's call on A1: likely confirmed if you do not reply.</p>
    </div>
  )
  return (
    <div className="ng-card" aria-label="An appeal check, as Nira shows it">
      <div className="ng-meta"><span className="nst-k">Appeal or not</span><span className="nst-mono">Order confirming ₹12,00,000</span></div>
      <p className="ng-v">Two points to raise, from the order's own text.</p>
      <div className="ng-foot three">
        <div><div className="ng-kk">Appeal by</div><div className="ng-vv">12 Jan 2027</div></div>
        <div><div className="ng-kk">Pre-deposit, s.107(6)</div><div className="ng-vv">₹1,20,000</div></div>
        <div><div className="ng-kk">Forum</div><div className="ng-strong">Commissioner (Appeals)</div></div>
      </div>
      <div className="ng-rows">
        <div className="ng-row"><span><span className="ng-n">1</span>The reply's e-way bills are not dealt with in the order.</span></div>
        <div className="ng-row"><span><span className="ng-n">2</span>Penalty under s.74 without a finding on intent.</span></div>
      </div>
    </div>
  )
}

export function SiteGlimpse({ intro }: { intro?: ReactNode }) {
  const navigate = useNavigate()
  const [which, setWhich] = useState<Sample>("time")
  const [text, setText] = useState(SAMPLES.time.question)
  const [note, setNote] = useState("")
  const composer = useRef<ComposerHandle>(null)
  const sample = SAMPLES[which]
  // The notice sample is a file, not words: the box shows it attached until the reader types.
  const showsFile = which === "notice" && !text

  const toApp = useCallback((path: string, state?: Record<string, unknown>) => {
    if (APP_URL) window.location.href = `${APP_URL}${path}`
    else navigate(path, state ? { state } : undefined)
  }, [navigate])

  /** Ask in the app: at once on the same origin; from the site's own origin the box is filled there and
   * the reader sends it (a link must never spend a reader's questions). A notice or an order goes to its door. */
  const ask = useCallback((question: string, door?: "notice" | "appeal") => {
    if (door && (!question || question === SAMPLES[which].question)) {
      track("site_question_sent", { sample: true, door })
      toApp(`/ask?door=${door}`)
      return
    }
    if (!question) { setNote("Type a question to start."); composer.current?.focus(); return }
    track("site_question_sent", { sample: ORDER.some((k) => SAMPLES[k].question === question) })
    if (APP_URL) window.location.href = `${APP_URL}/ask?ask=${encodeURIComponent(question)}`
    else navigate("/ask", { state: { ask: question } })
  }, [navigate, toApp, which])

  const pick = (k: Sample) => {
    setWhich(k)
    setText(SAMPLES[k].question)
    setNote("")
    track("site_sample_picked", {})
  }

  return (
    <div className="ng">
      <div className="ng-left">
        {intro}
        <div className="nl-landing nst-ask nst-rv nst-rv4">
          <Composer
            ref={composer}
            value={text}
            onChange={(v) => { setText(v); setNote("") }}
            onSubmit={() => ask(text.trim(), showsFile ? "notice" : undefined)}
            onFiles={(files) => { track("site_notice_attached", { files: files.length }); navigate("/ask", { state: { prefill: text.trim(), files } }) }}
            onAttach={APP_URL ? () => toApp("/ask?door=notice") : undefined}
            note={note}
            tray={showsFile ? <p className="ng-file"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10.3 17.6a1.6 1.6 0 0 1-2.3-2.3L16 7.3" /></svg>{SAMPLE_FILE}</p> : null}
          />
        </div>
        <div className="ng-try">
          <span className="ng-try-k">Try one</span>
          <div className="ng-chips">
            {ORDER.map((k) => (
              <button key={k} type="button" className={k === which ? "on" : ""} aria-pressed={k === which} onClick={() => pick(k)}>
                <span className="t">{SAMPLES[k].chip}</span>{SAMPLES[k].label}
              </button>
            ))}
          </div>
        </div>
        <p className="nst-small">Free GST research. No credit card needed. Your documents are read for your answer and nothing else.</p>
      </div>
      <div className="ng-right nst-rv nst-rv5" aria-live="polite">
        <div className="ng-head"><span className="nst-k">What comes back</span><span className="nst-mono">{sample.took}</span></div>
        <Card which={which} />
        <div className="ng-links">
          <button type="button" className="ng-open" onClick={() => ask(sample.question, sample.door)}>Open this reading in full →</button>
          <span className="ng-illus">Case names in examples are made up.</span>
        </div>
      </div>
    </div>
  )
}
